// Lazy-load + session cache for one detail-window block (P-1).
//
// `active` drives the fetch: the block is only requested the first time it becomes the selected
// one (idle -> loading), stays 'ready' without refetching while the window is open (S38), and
// only goes back to 'idle' — refetching on the next activation — via `invalidate()`, which the
// container calls after an action that changes that block's content (S31, e.g. saving General or
// changing status). `refresh()` is the background variant for the block on screen: it re-reads
// keeping 'ready' and the current data until the answer lands, so nothing unmounts meanwhile.
// A refresh that FAILS keeps 'ready' and that data too (stale-while-error, Task 29 fix r1): a
// background re-read never blanks what is on screen; the failure is exposed apart, in
// `refreshError` ({} when the last read went fine). `load()` keeps its 'error' state.
// Only the latest request may write the state — an older, slower answer is dropped. Unmounting the dialog drops this state entirely (RN9), so the cache never
// survives closing the window.
import { useCallback, useEffect, useRef, useState } from 'react'

const IDLE = { status: 'idle', data: {}, error: {}, refreshError: {} }

// `onInvalidate` (optional) runs on every `invalidate()`: the container uses it to drop what is cached
// outside the block and derived from the same data.
const useDetailBlock = (fetcher, active, onInvalidate = () => {}) => {
  const [state, setState] = useState(IDLE)
  const fetcherRef = useRef(fetcher)
  fetcherRef.current = fetcher
  const onInvalidateRef = useRef(onInvalidate)
  onInvalidateRef.current = onInvalidate

  const requestRef = useRef(0)
  const statusRef = useRef(state.status)
  statusRef.current = state.status

  const fetchLatest = useCallback(() => {
    const id = ++requestRef.current
    return fetcherRef.current()
      .then((data) => { if (id === requestRef.current) setState({ status: 'ready', data: data || {}, error: {}, refreshError: {} }) })
      .catch((error) => {
        if (id !== requestRef.current) return
        setState((previous) => (previous.status === 'ready'
          ? { ...previous, refreshError: error || {} }
          : { status: 'error', data: {}, error: error || {}, refreshError: {} }))
      })
  }, [])

  const load = useCallback(() => {
    setState({ status: 'loading', data: {}, error: {}, refreshError: {} })
    return fetchLatest()
  }, [fetchLatest])

  // A block never loaded (idle) has nothing on screen to keep: its own activation will load it.
  const refresh = useCallback(() => (statusRef.current === 'idle' ? Promise.resolve() : fetchLatest()), [fetchLatest])

  useEffect(() => {
    if (active && state.status === 'idle') {
      load()
    }
  }, [active, state.status, load])

  const invalidate = useCallback(() => {
    // An answer still in flight belongs to what was just invalidated (e.g. the previous order).
    requestRef.current += 1
    setState(IDLE)
    onInvalidateRef.current()
  }, [])

  return { status: state.status, data: state.data, error: state.error, refreshError: state.refreshError, retry: load, invalidate, refresh }
}

export default useDetailBlock
